import { create } from 'zustand';

interface MarketplaceActionState {
  showFavoritesOnly: boolean;
  selectedProcurementIds: Set<string>;
  selectedCompareIds: Set<string>;
  submittedInterestIds: Set<string>;
  submittedInterestNames: Record<string, string>;
  pageListingId: string | null;
  pageListingStatus: string | null;
  setShowFavoritesOnly: (v: boolean) => void;
  toggleProcurementId: (id: string) => void;
  clearProcurementIds: () => void;
  setProcurementIds: (ids: Set<string>) => void;
  toggleCompareId: (id: string) => void;
  clearCompareIds: () => void;
  addSubmittedInterest: (id: string, name?: string) => void;
  removeSubmittedInterest: (id: string) => void;
  setSubmittedInterests: (items: Array<{ id: string; name?: string }>) => void;
  setPageListingId: (id: string | null, status?: string | null) => void;
}

export const useMarketplaceActionStore = create<MarketplaceActionState>()((set) => ({
  showFavoritesOnly: false,
  selectedProcurementIds: new Set(),
  selectedCompareIds: new Set(),
  submittedInterestIds: new Set(),
  submittedInterestNames: {},
  pageListingId: null,
  pageListingStatus: null,
  setShowFavoritesOnly: (v) => set({ showFavoritesOnly: v }),
  toggleProcurementId: (id) => set((s) => {
    const next = new Set(s.selectedProcurementIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    return { selectedProcurementIds: next };
  }),
  clearProcurementIds: () => set({ selectedProcurementIds: new Set() }),
  setProcurementIds: (ids) => set({ selectedProcurementIds: ids }),
  toggleCompareId: (id) => set((s) => {
    const next = new Set(s.selectedCompareIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    return { selectedCompareIds: next };
  }),
  clearCompareIds: () => set({ selectedCompareIds: new Set() }),
  addSubmittedInterest: (id, name) => set((s) => ({
    submittedInterestIds: new Set([...s.submittedInterestIds, id]),
    submittedInterestNames: name
      ? { ...s.submittedInterestNames, [id]: name }
      : s.submittedInterestNames,
  })),
  removeSubmittedInterest: (id) => set((s) => {
    const nextIds = new Set(s.submittedInterestIds);
    nextIds.delete(id);
    const nextNames = { ...s.submittedInterestNames };
    delete nextNames[id];
    return { submittedInterestIds: nextIds, submittedInterestNames: nextNames };
  }),
  setSubmittedInterests: (items) => set(() => {
    const ids = new Set<string>();
    const names: Record<string, string> = {};
    items.forEach((item) => {
      ids.add(item.id);
      if (item.name) names[item.id] = item.name;
    });
    return { submittedInterestIds: ids, submittedInterestNames: names };
  }),
  setPageListingId: (id, status = null) => set({ pageListingId: id, pageListingStatus: status }),
}));
